import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { catchError, combineLatest, finalize, forkJoin, from, map, Observable, of, startWith, switchMap, take, tap } from 'rxjs';
import { AuthorizationService } from '../services/authorization.service';
import * as JwtDecode from 'jwt-decode';
import { capitalizeAllFirstLetters } from '../utils/strings';
import { StateService } from '../services/state.service';
import { StorageService } from '../services/storage.service';
import { LocalStorageKeys } from '../shared/types/storage';
import { ManagementService } from '../components/management-screen/management.service';
import { Permissions } from '../shared/permissions';
import { AUTH0_ROLE, UserProfileResponse } from '../shared/types/UserProfile';
import { RoutePaths } from '../shared/types/common';

function initializeUserProfile(user: UserProfileResponse, token: string, authorization: AuthorizationService, state: StateService, storage: StorageService, management: ManagementService): Observable<void> {
  if (authorization.profileInitiated) {
    return of(void 0);
  }
  state.loading = true;
  const decodedToken = JwtDecode.jwtDecode(token) as Record<string, any>;
  authorization.permissions = decodedToken['permissions'];

  const { id, email, firstName, lastName, account, role, trialDaysLeft, companyName } = user;
  authorization.userProfile = {
    id: id,
    email: email,
    firstName: capitalizeAllFirstLetters(firstName),
    lastName: capitalizeAllFirstLetters(lastName),
    role: role,
    account: account?.key,
    trialDaysLeft: trialDaysLeft,
    companyName: companyName
  };

  const accountKeys$ = 
    authorization.hasPermissions([Permissions.READ_ACCOUNT])
    ? management.getAccounts({ elaborate: false }).pipe( map(accounts => accounts.accounts.map(acc => acc.accountKey)) )
    : of([account?.key]);

  return accountKeys$.pipe(
    tap((accountKeys) => {
      const sessionAccount = storage.local.getItem(LocalStorageKeys.ACCOUNT);
      state.accounts = accountKeys.filter(key => key !== undefined);

      const activeOrg =
        sessionAccount && accountKeys.includes(sessionAccount)
          ? sessionAccount
          : (accountKeys ? accountKeys[0]: undefined);
      state.setActiveAccount(activeOrg as string);
    }),
    finalize(() => {
      state.loading = false;
    }),
    map(() => void 0)
  );
}

export const authGuard: CanActivateFn = (route, state) => {
  const auth = inject(AuthService);
  const authorization = inject(AuthorizationService);
  const managementService = inject(ManagementService);
  const stateService = inject(StateService);
  const router = inject(Router);
  const storageService = inject(StorageService);
  const requiredPermissions = route.data?.['permissions'] as string[] || [];
  const redirectTo = route.data?.['redirectTo'];
  const forceLogout = !redirectTo;

  return combineLatest([auth.isAuthenticated$, auth.error$.pipe(startWith(null))]).pipe(
    take(1),
    switchMap(([isAuthenticated, error]) => {
      if (error) {
        auth.logout({ logoutParams: { returnTo: window.location.origin } });
        return of(false);
      }
      if (!isAuthenticated) {
        auth.loginWithRedirect({ appState: { target: state.url }, authorizationParams: { scope: 'openid profile' } });
        return of(false);
      }

      stateService.loading = true;
      return stateService.getMyUser().pipe(
        switchMap((user) =>
          from(auth.getAccessTokenSilently({ cacheMode: 'off' })).pipe(
            switchMap((token) => {
              const shouldCompleteSignup = !user.role || user.role === AUTH0_ROLE.SIGNUP;
              if (shouldCompleteSignup) {
                router.navigate([RoutePaths.TRIAL], { queryParams: { user: JSON.stringify(user), token: token } });
                return of(false);
              }
              return initializeUserProfile(user, token, authorization, stateService, storageService, managementService).pipe(
                map(() => {
                  const hasAccess = authorization.hasPermissions(requiredPermissions);
                  if (!hasAccess) {
                    if (forceLogout) {
                      auth.logout({ logoutParams: { returnTo: window.location.origin } });
                    } else {
                      router.navigateByUrl(redirectTo!);
                    }
                    return false;
                  }
                  return true;
                })
              );
            })
          )
        ),
        catchError((error) => {
          auth.logout({ logoutParams: { returnTo: window.location.origin } });
          return of(false);
        }),
        finalize(() => {
          stateService.loading = false;
        })
      );
    })
  )
}
